//----------------------------------------------------------------------------------------------------------------------
// Game Engine
//
// WARNING: This module must be imported as `import type` everywhere except sage.ts. A regular import
// would create a circular runtime dependency (managers import GameEngine, GameEngine imports managers).
// The `import type` is erased at compile time, keeping the runtime graph acyclic.
//----------------------------------------------------------------------------------------------------------------------

import { AbstractEngine, HavokPlugin } from '@babylonjs/core';

// Interfaces
import type { GameCanvas } from '../interfaces/game.ts';
import type { Disposable } from '../interfaces/lifecycle.ts';
import type { LoggerInterface } from '../interfaces/logger.ts';

// Engines
import { SceneEngine } from '../engines/scene.ts';
import type { AudioEngine } from '../engines/audio.ts';

// Managers
import { AssetManager } from '../managers/asset.ts';
import { BindingManager } from '../managers/binding.ts';
import type { ColliderDebugManager } from '../managers/colliderDebug.ts';
import type { DebugConsole } from './debugConsole.ts';
import { GameManager } from '../managers/game.ts';
import { GameEntityManager } from '../managers/entity.ts';
import { LevelManager } from '../managers/level.ts';
import { SaveManager } from '../managers/save.ts';
import { UserInputManager } from '../managers/input.ts';
import type { AudioManager } from '../managers/audio.ts';

// Utils
import { type GameEvent, GameEventBus, type GameEventCallback, type Unsubscribe } from './eventBus.ts';
import type { ActionPayload, LibraryEventPayloadMap } from '../events/payloads.ts';
import type { WildcardPattern } from '../events/types.ts';
import { LoggingUtility } from '../utils/logger.ts';
import { RaycastHelper } from '../utils/raycast.ts';
import { GameTimer } from '../utils/timer.ts';
import { VERSION } from '../utils/version.ts';

//----------------------------------------------------------------------------------------------------------------------

/**
 * Type definition for game lifecycle hook functions
 */
 
export type GameHook = (gameEngine : GameEngine) => Promise<void>;

/**
 * Interface representing the engines used in the game.
 * All engines must implement Disposable for proper cleanup.
 */
interface Engines extends Record<string, Disposable | undefined>
{
    sceneEngine : SceneEngine;
    audioEngine ?: AudioEngine;
}

/**
 * Interface representing the managers used in the game.
 * All managers must implement Disposable for proper cleanup.
 */
interface Managers extends Record<string, Disposable | undefined>
{
    assetManager : AssetManager;
    bindingManager : BindingManager;
    gameManager : GameManager;
    entityManager : GameEntityManager;
    inputManager : UserInputManager;
    levelManager : LevelManager;
    saveManager : SaveManager;
    audioManager ?: AudioManager;
}

/**
 * Interface representing the debug tools available on the engine.
 */
interface DebugTools
{
    colliders : ColliderDebugManager;
    console : DebugConsole | null;
    expose(name : string, value : unknown) : void;
}

//----------------------------------------------------------------------------------------------------------------------

/**
 * Central hub that owns the render loop, physics, event bus, and all managers.
 * Created via `createGameEngine()` in sage.ts.
 */
export class GameEngine
{
    public canvas : GameCanvas;
    public renderEngine : AbstractEngine;
    public physics : HavokPlugin;
    public managers : Managers;
    public engines : Engines;
    public eventBus : GameEventBus;
    public logger : LoggingUtility;
    public raycast : RaycastHelper;
    public timer : GameTimer;
    public debug : DebugTools;
    public largeWorldRendering : boolean;
    public started = false;

    private _log : LoggerInterface;

    // Lifecycle hooks
    private _beforeStartHook : GameHook | null = null;
    private _onStartHook : GameHook | null = null;
    private _onTeardownHook : GameHook | null = null;

    /**
     * Creates an instance of GameEngine.
     * @param canvas
     * @param renderEngine
     * @param physics
     * @param eventBus
     * @param logger
     * @param engines
     * @param managers
     */
    constructor(
        canvas : GameCanvas,
        renderEngine : AbstractEngine,
        physics : HavokPlugin,
        eventBus : GameEventBus,
        logger : LoggingUtility,
        raycast : RaycastHelper,
        timer : GameTimer,
        engines : Engines,
        managers : Managers,
        largeWorldRendering = false,
        debug ?: DebugTools
    )
    {
        this.canvas = canvas;
        this.renderEngine = renderEngine;
        this.physics = physics;
        this.eventBus = eventBus;
        this.logger = logger;
        this.raycast = raycast;
        this.timer = timer;
        this.engines = engines;
        this.managers = managers;
        this.debug = debug ?? {
            colliders: undefined as unknown as ColliderDebugManager,
            console: null,
            expose: () => { /* no-op */ },
        };
        this.largeWorldRendering = largeWorldRendering;

        this._log = logger.getLogger('GameEngine');
    }

    //------------------------------------------------------------------------------------------------------------------
    // Public API
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Register a function to be called before the game engine starts
     * @param hook
     * @throws Error if a hook is already registered
     */
    onBeforeStart(hook : GameHook) : void
    {
        if(this._beforeStartHook !== null)
        {
            throw new Error('A beforeStart hook is already registered. Only one hook is allowed per lifecycle event.');
        }
        this._beforeStartHook = hook;
    }

    /**
     * Register a function to be called after the game engine starts
     * @param hook
     * @throws Error if a hook is already registered
     */
    onStart(hook : GameHook) : void
    {
        if(this._onStartHook !== null)
        {
            throw new Error('An onStart hook is already registered. Only one hook is allowed per lifecycle event.');
        }
        this._onStartHook = hook;
    }

    /**
     * Register a function to be called when the game engine stops
     * @param hook
     * @throws Error if a hook is already registered
     */
    onTeardown(hook : GameHook) : void
    {
        if(this._onTeardownHook !== null)
        {
            throw new Error('An onTeardown hook is already registered. Only one hook is allowed per lifecycle event.');
        }
        this._onTeardownHook = hook;
    }

    //------------------------------------------------------------------------------------------------------------------
    // Event Subscriptions
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Subscribe to an event on the event bus.
     *
     * This is a convenience method that forwards to `eventBus.subscribe()`.
     *
     * @param eventType - Exact event type string, wildcard pattern (e.g. 'input:*'), or RegExp
     * @param callback
     * @returns A function that removes this subscription when called
     *
     * @example
     * // Subscribe to a specific event
     * const unsub = engine.subscribe('level:complete', (event) => {
     *     console.log('Level complete:', event.payload.levelName);
     * });
     *
     * // Subscribe to all input events
     * engine.subscribe('input:*', (event) => {
     *     console.log('Input event:', event.type);
     * });
     */
    subscribe<T extends keyof LibraryEventPayloadMap & string>(
        eventType : T | WildcardPattern | RegExp,
        callback : GameEventCallback<T, LibraryEventPayloadMap>
    ) : Unsubscribe
    {
        return this.eventBus.subscribe(eventType, callback);
    }

    /**
     * Subscribe to an action event with full type safety.
     *
     * Action events are fired by the binding system when inputs trigger registered actions.
     * All action events have the same payload structure (`ActionPayload`), containing:
     * - `value`: The action value (number for analog, boolean for digital)
     * - `deviceId`: The ID of the input device that triggered the action
     * - `context`: The binding context that was active (optional)
     *
     * @param actionName - The action name (without the 'action:' prefix)
     * @param callback
     * @returns A function that removes this subscription when called
     *
     * @example
     * // Subscribe to a specific action
     * engine.subscribeAction('jump', (event) => {
     *     console.log('Jump triggered with value:', event.payload.value);
     *     console.log('From device:', event.payload.deviceId);
     * });
     *
     * // Subscribe to movement action
     * engine.subscribeAction('moveForward', (event) => {
     *     const velocity = Number(event.payload.value);
     *     player.move(velocity);
     * });
     */
    subscribeAction(
        actionName : string,
        callback : (event : GameEvent<`action:${ string }`, Record<`action:${ string }`, ActionPayload>>) => void
    ) : Unsubscribe
    {
        return this.eventBus.subscribe(
            `action:${ actionName }` as keyof LibraryEventPayloadMap & string,
            callback as unknown as GameEventCallback<keyof LibraryEventPayloadMap & string, LibraryEventPayloadMap>
        );
    }

    //------------------------------------------------------------------------------------------------------------------
    // Lifecycle
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Start the engine: runs beforeStart hook, starts the game manager, then runs onStart hook.
     */
    async start() : Promise<void>
    {
        if(!this.started)
        {
            this._log.info(`Starting SkewedAspect Game Engine (Version: ${ VERSION })...`);

            // Execute beforeStart hook if registered
            if(this._beforeStartHook)
            {
                this._log.debug('Executing beforeStart hook...');
                await this._beforeStartHook(this);
            }

            // Start the game manager
            await this.managers.gameManager.start();

            this._log.info('Game engine started successfully');

            // Execute onStart hook if registered
            if(this._onStartHook)
            {
                this._log.debug('Executing onStart hook...');
                await this._onStartHook(this);
            }

            // Mark game as started
            this.started = true;
        }
        else
        {
            this._log.warn('Game engine is already started. Skipping start.');
        }
    }

    /**
     * Stop the engine: runs the teardown hook, then tears down all managers and engines in order.
     */
    async stop() : Promise<void>
    {
        if(this.started)
        {
            this._log.info(`Stopping SkewedAspect Game Engine (Version: ${ VERSION })...`);

            let teardownError : Error | null = null;

            // Execute onTeardown hook if registered
            if(this._onTeardownHook)
            {
                try
                {
                    this._log.debug('Executing onTeardown hook...');
                    await this._onTeardownHook(this);
                }
                catch (err)
                {
                    this._log.error(`Error in onTeardown hook: ${ err }`);
                    teardownError = teardownError || (err as Error);
                }
            }

            // Dispose debug console before tearing down managers
            if(this.debug.console)
            {
                this.debug.console.dispose();
            }

            // Teardown all managers
            for(const managerKey of Object.keys(this.managers) as (keyof Managers)[])
            {
                const manager = this.managers[managerKey];
                if(manager)
                {
                    try
                    {
                        this._log.debug(`Tearing down manager: ${ managerKey }`);
                        // eslint-disable-next-line no-await-in-loop
                        await manager.$teardown();
                    }
                    catch (err)
                    {
                        this._log.error(`Error tearing down manager ${ managerKey }: ${ err }`);
                        teardownError = teardownError || (err as Error);
                    }
                }
            }

            // Teardown all engines
            for(const engineKey of Object.keys(this.engines) as (keyof Engines)[])
            {
                const engine = this.engines[engineKey];
                if(engine)
                {
                    try
                    {
                        this._log.debug(`Tearing down engine: ${ engineKey }`);
                        // eslint-disable-next-line no-await-in-loop
                        await engine.$teardown();
                    }
                    catch (err)
                    {
                        this._log.error(`Error tearing down engine ${ engineKey }: ${ err }`);
                        teardownError = teardownError || (err as Error);
                    }
                }
            }

            this.started = false;
            this._log.info('Game engine stopped successfully');

            // If we encountered any errors during teardown, throw the first one
            if(teardownError)
            {
                throw teardownError;
            }
        }
        else
        {
            this._log.warn('Game engine is not started. Skipping stop.');
        }
    }
}

//----------------------------------------------------------------------------------------------------------------------
